import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { AlertTriangleIcon, CheckIcon, XIcon, SearchIcon, FilterIcon, CalendarIcon, ClockIcon, MapPinIcon, UsersIcon, MessageSquareIcon, PhoneIcon, EditIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
// Mock data for conflicts
const mockConflicts = [{
  id: 1,
  type: 'double_booking',
  status: 'unresolved',
  severity: 'high',
  description: 'Instructeur Erik de Vries is ingepland voor twee trainingen op hetzelfde tijdstip',
  date: new Date(new Date().setDate(new Date().getDate() + 1)),
  affectedTrainings: [{
    id: 101,
    type: 'Sliptraining Basis',
    location: 'Rotterdam',
    startTime: new Date(new Date().setDate(new Date().getDate() + 1)).setHours(9, 0),
    endTime: new Date(new Date().setDate(new Date().getDate() + 1)).setHours(12, 0),
    client: 'Gemeente Rotterdam',
    instructor: 'Erik de Vries',
    participants: 8
  }, {
    id: 102,
    type: 'Defensief Rijden',
    location: 'Utrecht',
    startTime: new Date(new Date().setDate(new Date().getDate() + 1)).setHours(9, 30),
    endTime: new Date(new Date().setDate(new Date().getDate() + 1)).setHours(12, 30),
    client: 'ProRail',
    instructor: 'Erik de Vries',
    participants: 6
  }],
  possibleSolutions: ['Andere instructeur toewijzen aan één van de trainingen', 'Een van de trainingen verplaatsen naar een andere datum/tijd', 'Contact opnemen met klant voor herschikking']
}, {
  id: 2,
  type: 'sick_instructor',
  status: 'unresolved',
  severity: 'high',
  description: 'Instructeur Lisa Bakker heeft zich ziek gemeld voor aankomende trainingen',
  date: new Date(),
  affectedTrainings: [{
    id: 103,
    type: 'Code 95 Training',
    location: 'Amsterdam',
    startTime: new Date(new Date().setDate(new Date().getDate() + 2)).setHours(9, 0),
    endTime: new Date(new Date().setDate(new Date().getDate() + 2)).setHours(13, 0),
    client: 'Transport BV',
    instructor: 'Lisa Bakker',
    participants: 12
  }, {
    id: 104,
    type: 'Defensief Rijden',
    location: 'Utrecht',
    startTime: new Date(new Date().setDate(new Date().getDate() + 3)).setHours(13, 30),
    endTime: new Date(new Date().setDate(new Date().getDate() + 3)).setHours(16, 30),
    client: 'ProRail',
    instructor: 'Lisa Bakker',
    participants: 6
  }],
  possibleSolutions: ['Vervangende instructeur toewijzen', 'Trainingen verplaatsen naar een latere datum', 'Contact opnemen met klant voor annulering']
}, {
  id: 3,
  type: 'location_unavailable',
  status: 'in_progress',
  severity: 'medium',
  description: 'Locatie Rotterdam is niet beschikbaar vanwege onderhoud',
  date: new Date(new Date().setDate(new Date().getDate() + 5)),
  affectedTrainings: [{
    id: 105,
    type: 'Sliptraining Gevorderd',
    location: 'Rotterdam',
    startTime: new Date(new Date().setDate(new Date().getDate() + 5)).setHours(9, 0),
    endTime: new Date(new Date().setDate(new Date().getDate() + 5)).setHours(12, 0),
    client: 'Shell Nederland',
    instructor: 'Erik de Vries',
    participants: 6
  }],
  possibleSolutions: ['Training verplaatsen naar een andere locatie', 'Training verplaatsen naar een andere datum', 'Contact opnemen met klant voor herschikking'],
  notes: 'Contact opgenomen met klant, wachten op bevestiging voor nieuwe datum'
}, {
  id: 4,
  type: 'no_show',
  status: 'resolved',
  severity: 'low',
  description: 'Klant is niet komen opdagen voor training',
  date: new Date(new Date().setDate(new Date().getDate() - 1)),
  affectedTrainings: [{
    id: 106,
    type: 'Defensief Rijden',
    location: 'Amsterdam',
    startTime: new Date(new Date().setDate(new Date().getDate() - 1)).setHours(9, 0),
    endTime: new Date(new Date().setDate(new Date().getDate() - 1)).setHours(12, 0),
    client: 'Logistiek BV',
    instructor: 'Peter Jansen',
    participants: 4
  }],
  possibleSolutions: ['Contact opnemen met klant', 'Factuur sturen conform no-show beleid', 'Nieuwe training inplannen'],
  resolution: 'Klant gecontacteerd, factuur verstuurd, nieuwe training ingepland voor volgende maand'
}];
// Helper function to format time
const formatTime = date => {
  return new Date(date).toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit'
  });
};
// Helper function to format date
const formatDate = date => {
  return date.toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
};
// Helper function to get badge color for conflict type
const getConflictTypeBadge = type => {
  switch (type) {
    case 'double_booking':
      return <Badge variant="danger" data-id="element-2200">Dubbele Boeking</Badge>;
    case 'sick_instructor':
      return <Badge variant="warning" data-id="element-2201">Zieke Instructeur</Badge>;
    case 'location_unavailable':
      return <Badge variant="info" data-id="element-2202">Locatie Onbeschikbaar</Badge>;
    case 'no_show':
      return <Badge variant="default" data-id="element-2203">No-Show</Badge>;
    default:
      return <Badge variant="default" data-id="element-2204">Overig</Badge>;
  }
};
// Helper function to get badge color for conflict status
const getConflictStatusBadge = status => {
  switch (status) {
    case 'unresolved':
      return <Badge variant="danger" data-id="element-2205">Onopgelost</Badge>;
    case 'in_progress':
      return <Badge variant="warning" data-id="element-2206">In Behandeling</Badge>;
    case 'resolved':
      return <Badge variant="success" data-id="element-2207">Opgelost</Badge>;
    default:
      return <Badge variant="default" data-id="element-2208">Onbekend</Badge>;
  }
};
// Helper function to get badge color for conflict severity
const getConflictSeverityBadge = severity => {
  switch (severity) {
    case 'high':
      return <Badge variant="danger" data-id="element-2209">Hoog</Badge>;
    case 'medium':
      return <Badge variant="warning" data-id="element-2210">Middel</Badge>;
    case 'low':
      return <Badge variant="info" data-id="element-2211">Laag</Badge>;
    default:
      return <Badge variant="default" data-id="element-2212">Onbekend</Badge>;
  }
};
export function Conflicts() {
  const [conflicts, setConflicts] = useState(mockConflicts);
  const [expandedConflict, setExpandedConflict] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showResolutionModal, setShowResolutionModal] = useState(false);
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');
  // Filter conflicts based on filters and search
  const filteredConflicts = conflicts.filter(conflict => {
    // Filter by status
    if (filterStatus !== 'all' && conflict.status !== filterStatus) {
      return false;
    }
    // Filter by type
    if (filterType !== 'all' && conflict.type !== filterType) {
      return false;
    }
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return conflict.description.toLowerCase().includes(searchLower) || conflict.affectedTrainings.some(training => training.type.toLowerCase().includes(searchLower) || training.location.toLowerCase().includes(searchLower) || training.client.toLowerCase().includes(searchLower) || training.instructor.toLowerCase().includes(searchLower));
    }
    return true;
  });
  // Toggle conflict details
  const toggleConflictDetails = conflictId => {
    if (expandedConflict === conflictId) {
      setExpandedConflict(null);
    } else {
      setExpandedConflict(conflictId);
    }
  };
  // Handle conflict resolution
  const handleResolveConflict = conflict => {
    setSelectedConflict(conflict);
    setResolutionNote('');
    setShowResolutionModal(true);
  };
  // Submit conflict resolution
  const submitResolution = () => {
    if (!selectedConflict || !resolutionNote) return;
    // Update conflict status to resolved
    const updatedConflicts = conflicts.map(conflict => {
      if (conflict.id === selectedConflict.id) {
        return {
          ...conflict,
          status: 'resolved',
          resolution: resolutionNote
        };
      }
      return conflict;
    });
    setConflicts(updatedConflicts);
    setShowResolutionModal(false);
    setSelectedConflict(null);
    setResolutionNote('');
  };
  // Mark conflict as in progress
  const markAsInProgress = conflictId => {
    const updatedConflicts = conflicts.map(conflict => {
      if (conflict.id === conflictId) {
        return {
          ...conflict,
          status: 'in_progress'
        };
      }
      return conflict;
    });
    setConflicts(updatedConflicts);
  };
  return <div className="space-y-6" data-id="element-2213">
      <div className="flex justify-between items-center" data-id="element-2214">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2215">
          Problemen & Conflicten
        </h1>
        <Button variant="outline" size="sm" onClick={() => {
        // Refresh conflicts (in a real app, this would fetch from the server)
        setConflicts([...mockConflicts]);
      }} data-id="element-2216">
          <div className="h-4 w-4 mr-1" data-id="element-2217" />
          Vernieuwen
        </Button>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2218">
        <div className="col-span-1" data-id="element-2219">
          <div className="relative" data-id="element-2220">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-2221" />
            <input type="text" placeholder="Zoeken..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#B61C1C]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2222" />
          </div>
        </div>
        <div className="col-span-1" data-id="element-2223">
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#B61C1C]" data-id="element-2224">
            <option value="all" data-id="element-2225">Alle statussen</option>
            <option value="unresolved" data-id="element-2226">Onopgelost</option>
            <option value="in_progress" data-id="element-2227">In Behandeling</option>
            <option value="resolved" data-id="element-2228">Opgelost</option>
          </select>
        </div>
        <div className="col-span-1" data-id="element-2229">
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#B61C1C]" data-id="element-2230">
            <option value="all" data-id="element-2231">Alle types</option>
            <option value="double_booking" data-id="element-2232">Dubbele Boeking</option>
            <option value="sick_instructor" data-id="element-2233">Zieke Instructeur</option>
            <option value="location_unavailable" data-id="element-2234">Locatie Onbeschikbaar</option>
            <option value="no_show" data-id="element-2235">No-Show</option>
          </select>
        </div>
      </div>
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-2236">
        <Card className="col-span-1 p-4" data-id="element-2237">
          <div className="flex items-center" data-id="element-2238">
            <div className="p-2 rounded-full bg-gray-100" data-id="element-2239">
              <AlertTriangleIcon className="h-5 w-5 text-gray-600" data-id="element-2240" />
            </div>
            <div className="ml-3" data-id="element-2241">
              <div className="text-sm text-gray-500" data-id="element-2242">Totaal</div>
              <div className="text-xl font-bold" data-id="element-2243">{conflicts.length}</div>
            </div>
          </div>
        </Card>
        <Card className="col-span-1 p-4" data-id="element-2244">
          <div className="flex items-center" data-id="element-2245">
            <div className="p-2 rounded-full bg-red-100" data-id="element-2246">
              <XIcon className="h-5 w-5 text-red-600" data-id="element-2247" />
            </div>
            <div className="ml-3" data-id="element-2248">
              <div className="text-sm text-gray-500" data-id="element-2249">Onopgelost</div>
              <div className="text-xl font-bold" data-id="element-2250">
                {conflicts.filter(c => c.status === 'unresolved').length}
              </div>
            </div>
          </div>
        </Card>
        <Card className="col-span-1 p-4" data-id="element-2251">
          <div className="flex items-center" data-id="element-2252">
            <div className="p-2 rounded-full bg-yellow-100" data-id="element-2253">
              <div className="h-5 w-5 text-yellow-600" data-id="element-2254" />
            </div>
            <div className="ml-3" data-id="element-2255">
              <div className="text-sm text-gray-500" data-id="element-2256">In Behandeling</div>
              <div className="text-xl font-bold" data-id="element-2257">
                {conflicts.filter(c => c.status === 'in_progress').length}
              </div>
            </div>
          </div>
        </Card>
        <Card className="col-span-1 p-4" data-id="element-2258">
          <div className="flex items-center" data-id="element-2259">
            <div className="p-2 rounded-full bg-green-100" data-id="element-2260">
              <CheckIcon className="h-5 w-5 text-green-600" data-id="element-2261" />
            </div>
            <div className="ml-3" data-id="element-2262">
              <div className="text-sm text-gray-500" data-id="element-2263">Opgelost</div>
              <div className="text-xl font-bold" data-id="element-2264">
                {conflicts.filter(c => c.status === 'resolved').length}
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Conflicts list */}
      <Card data-id="element-2265">
        <div className="p-4 border-b border-gray-200" data-id="element-2266">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-2267">Conflicten</h2>
        </div>
        <div data-id="element-2268">
          {filteredConflicts.length > 0 ? <div className="divide-y divide-gray-200" data-id="element-2269">
              {filteredConflicts.map(conflict => <div key={conflict.id} className="p-4" data-id="element-2270">
                  <div className="flex justify-between items-start cursor-pointer" onClick={() => toggleConflictDetails(conflict.id)} data-id="element-2271">
                    <div data-id="element-2272">
                      <div className="flex items-center space-x-2" data-id="element-2273">
                        {getConflictTypeBadge(conflict.type)}
                        {getConflictStatusBadge(conflict.status)}
                        {getConflictSeverityBadge(conflict.severity)}
                      </div>
                      <h3 className="mt-1 text-sm font-medium text-gray-900" data-id="element-2274">
                        {conflict.description}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-2275">
                        <CalendarIcon className="inline-block h-3 w-3 mr-1" data-id="element-2276" />
                        {formatDate(conflict.date)} |{' '}
                        {conflict.affectedTrainings.length} trainingen getroffen
                      </p>
                    </div>
                    <div className="flex items-center" data-id="element-2277">
                      {conflict.status === 'unresolved' && <Button variant="outline" size="sm" className="mr-2" onClick={e => {
                  e.stopPropagation();
                  markAsInProgress(conflict.id);
                }} data-id="element-2278">
                          <div className="h-4 w-4 mr-1" data-id="element-2279" />
                          In Behandeling
                        </Button>}
                      {conflict.status !== 'resolved' && <Button variant="primary" size="sm" onClick={e => {
                  e.stopPropagation();
                  handleResolveConflict(conflict);
                }} data-id="element-2280">
                          <CheckIcon className="h-4 w-4 mr-1" data-id="element-2281" />
                          Oplossen
                        </Button>}
                      <button className="ml-2 text-gray-400 hover:text-gray-500" onClick={e => {
                  e.stopPropagation();
                  toggleConflictDetails(conflict.id);
                }} data-id="element-2282">
                        {expandedConflict === conflict.id ? <ChevronUpIcon className="h-5 w-5" data-id="element-2283" /> : <ChevronDownIcon className="h-5 w-5" data-id="element-2284" />}
                      </button>
                    </div>
                  </div>
                  {/* Expanded details */}
                  {expandedConflict === conflict.id && <div className="mt-4 pl-4 border-l-2 border-gray-200" data-id="element-2285">
                      <div className="space-y-4" data-id="element-2286">
                        <div data-id="element-2287">
                          <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2288">
                            Getroffen Trainingen
                          </h4>
                          <div className="space-y-2" data-id="element-2289">
                            {conflict.affectedTrainings.map(training => <div key={training.id} className="p-3 bg-gray-50 rounded-md" data-id="element-2290">
                                <div className="flex justify-between items-start" data-id="element-2291">
                                  <div data-id="element-2292">
                                    <h5 className="text-sm font-medium text-gray-900" data-id="element-2293">
                                      {training.type}
                                    </h5>
                                    <p className="text-xs text-gray-500" data-id="element-2294">
                                      {training.client}
                                    </p>
                                    <div className="flex items-center mt-1 text-xs text-gray-500" data-id="element-2295">
                                      <CalendarIcon className="h-3 w-3 mr-1" data-id="element-2296" />
                                      <span data-id="element-2297">
                                        {formatDate(new Date(training.startTime))}
                                      </span>
                                    </div>
                                    <div className="flex items-center mt-1 text-xs text-gray-500" data-id="element-2298">
                                      <ClockIcon className="h-3 w-3 mr-1" data-id="element-2299" />
                                      <span data-id="element-2300">
                                        {formatTime(training.startTime)} -{' '}
                                        {formatTime(training.endTime)}
                                      </span>
                                    </div>
                                    <div className="flex items-center mt-1 text-xs text-gray-500" data-id="element-2301">
                                      <MapPinIcon className="h-3 w-3 mr-1" data-id="element-2302" />
                                      <span data-id="element-2303">{training.location}</span>
                                    </div>
                                  </div>
                                  <div data-id="element-2304">
                                    <div className="text-xs text-gray-500" data-id="element-2305">
                                      <UsersIcon className="inline-block h-3 w-3 mr-1" data-id="element-2306" />
                                      {training.participants} deelnemers
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1" data-id="element-2307">
                                      Instructeur: {training.instructor}
                                    </div>
                                  </div>
                                </div>
                              </div>)}
                          </div>
                        </div>
                        <div data-id="element-2308">
                          <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2309">
                            Mogelijke Oplossingen
                          </h4>
                          <ul className="list-disc list-inside space-y-1" data-id="element-2310">
                            {conflict.possibleSolutions.map((solution, index) => <li key={index} className="text-sm text-gray-600" data-id="element-2311">
                                  {solution}
                                </li>)}
                          </ul>
                        </div>
                        {conflict.status === 'in_progress' && conflict.notes && <div data-id="element-2312">
                              <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2313">
                                Voortgangsnotities
                              </h4>
                              <div className="p-3 bg-blue-50 border border-blue-100 rounded-md" data-id="element-2314">
                                <p className="text-sm text-blue-800" data-id="element-2315">
                                  {conflict.notes}
                                </p>
                              </div>
                            </div>}
                        {conflict.status === 'resolved' && conflict.resolution && <div data-id="element-2316">
                              <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2317">
                                Oplossing
                              </h4>
                              <div className="p-3 bg-green-50 border border-green-100 rounded-md" data-id="element-2318">
                                <p className="text-sm text-green-800" data-id="element-2319">
                                  {conflict.resolution}
                                </p>
                              </div>
                            </div>}
                        {/* Actions */}
                        <div className="flex justify-end space-x-2" data-id="element-2320">
                          <Button variant="outline" size="sm" data-id="element-2321">
                            <MessageSquareIcon className="h-4 w-4 mr-1" data-id="element-2322" />
                            Notitie Toevoegen
                          </Button>
                          <Button variant="outline" size="sm" data-id="element-2323">
                            <PhoneIcon className="h-4 w-4 mr-1" data-id="element-2324" />
                            Contact Opnemen
                          </Button>
                          <Button variant="outline" size="sm" data-id="element-2325">
                            <EditIcon className="h-4 w-4 mr-1" data-id="element-2326" />
                            Training Bewerken
                          </Button>
                        </div>
                      </div>
                    </div>}
                </div>)}
            </div> : <div className="p-6 text-center" data-id="element-2327">
              <AlertTriangleIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" data-id="element-2328" />
              <h3 className="text-lg font-medium text-gray-900" data-id="element-2329">
                Geen conflicten gevonden
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-2330">
                Er zijn geen conflicten die voldoen aan je zoekcriteria
              </p>
            </div>}
        </div>
      </Card>
      {/* Resolution Modal */}
      {showResolutionModal && selectedConflict && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2331">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" data-id="element-2332">
            <div className="p-4 border-b border-gray-200" data-id="element-2333">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-2334">
                Conflict Oplossen
              </h3>
            </div>
            <div className="p-6" data-id="element-2335">
              <div className="mb-4" data-id="element-2336">
                <h4 className="text-sm font-medium text-gray-900 mb-1" data-id="element-2337">
                  Conflict
                </h4>
                <p className="text-sm text-gray-600" data-id="element-2338">
                  {selectedConflict.description}
                </p>
              </div>
              <div className="mb-4" data-id="element-2339">
                <label htmlFor="resolution" className="block text-sm font-medium text-gray-900 mb-1" data-id="element-2340">
                  Oplossing*
                </label>
                <textarea id="resolution" rows={4} className="w-full p-2 border border-gray-300 rounded-md" placeholder="Beschrijf hoe het conflict is opgelost..." value={resolutionNote} onChange={e => setResolutionNote(e.target.value)} data-id="element-2341"></textarea>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-md mb-4" data-id="element-2342">
                <div className="flex" data-id="element-2343">
                  <AlertTriangleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-2344" />
                  <p className="text-sm text-blue-800" data-id="element-2345">
                    Let op: Het markeren van een conflict als opgelost is
                    definitief. Zorg ervoor dat alle betrokken partijen op de
                    hoogte zijn van de oplossing.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2 p-4 border-t border-gray-200 bg-gray-50" data-id="element-2346">
              <Button variant="outline" onClick={() => setShowResolutionModal(false)} data-id="element-2347">
                <XIcon className="h-4 w-4 mr-1" data-id="element-2348" />
                Annuleren
              </Button>
              <Button variant="primary" onClick={submitResolution} disabled={!resolutionNote} data-id="element-2349">
                <CheckIcon className="h-4 w-4 mr-1" data-id="element-2350" />
                Conflict Oplossen
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}